import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { motion } from 'motion/react';
import { useCreateArticle } from '@/queries/articles';
import { ArticleEditor } from '@/components/ArticleEditor';
import { TopicSelect } from '@/components/TopicSelect';
import { TagInput } from '@/components/TagInput';
import ArrowLeftIcon from '@/assets/common/arrow-left.svg?react';
import styles from './create.module.scss';

export const Route = createFileRoute('/articles/create')({
  component: CreateArticlePage,
  beforeLoad: ({ context }) => {
    const { user, authLoading } = context as { user: any; authLoading: boolean };
    if (authLoading) return;
    if (!user) {
      throw redirect({ to: '/' });
    }
  },
});

function CreateArticlePage() {
  const navigate = useNavigate();
  const createArticle = useCreateArticle();

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [topic, setTopic] = useState<string | null>(null);
  const [tags, setTags] = useState<string[]>([]);
  const [isPublic, setIsPublic] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) return;

    createArticle.mutate(
      {
        title: title.trim(),
        content,
        topic: topic || undefined,
        tags,
        is_public: isPublic,
      },
      {
        onSuccess: (response) => {
          navigate({
            to: '/articles/$articleId',
            params: { articleId: response.articleId },
          });
        },
      }
    );
  };

  const isValid = title.trim().length > 0 && content.trim().length > 0;

  return (
    <div className={styles.createArticle}>
      <div className={styles.createArticle__header}>
        <button
          onClick={() => navigate({ to: '/articles' })}
          className={styles.createArticle__backButton}
        >
          <ArrowLeftIcon />
          Back to Articles
        </button>
        <h1 className={styles.createArticle__title}>Create Article</h1>
      </div>

      <form onSubmit={handleSubmit} className={styles.createArticle__form}>
        <div className={styles.createArticle__field}>
          <label className={styles.createArticle__label}>Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter article title..."
            className={styles.createArticle__input}
          />
        </div>

        <div className={styles.createArticle__field}>
          <label className={styles.createArticle__label}>Content</label>
          <ArticleEditor value={content} onChange={setContent} />
        </div>

        <div className={styles.createArticle__row}>
          <div className={styles.createArticle__field}>
            <label className={styles.createArticle__label}>Topic</label>
            <TopicSelect value={topic} onChange={setTopic} />
          </div>

          <div className={styles.createArticle__field}>
            <label className={styles.createArticle__label}>Tags</label>
            <TagInput value={tags} onChange={setTags} />
          </div>
        </div>

        <div className={styles.createArticle__field}>
          <label className={styles.createArticle__checkbox}>
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
            />
            <span>Make this article public</span>
          </label>
          <p className={styles.createArticle__hint}>
            Public articles can be viewed by anyone and receive comments
          </p>
        </div>

        <div className={styles.createArticle__actions}>
          <button
            type="button"
            onClick={() => navigate({ to: '/articles' })}
            className={styles.createArticle__cancelButton}
          >
            Cancel
          </button>
          <motion.button
            type="submit"
            className={styles.createArticle__submitButton}
            disabled={!isValid || createArticle.isPending}
            whileHover={isValid ? { scale: 1.02 } : {}}
            whileTap={isValid ? { scale: 0.98 } : {}}
          >
            {createArticle.isPending ? 'Creating...' : 'Create Article'}
          </motion.button>
        </div>
      </form>
    </div>
  );
}
